import React, { useMemo, useState } from "react";
import { ProCard, ProForm, ProFormRadio, ProFormSelect, ProFormText, StepsForm } from "@ant-design/pro-components";
import { Alert, Button, Space, Typography, message } from "antd";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { WorkflowAnswers, WorkflowOutcome, TopIntent, NewFirstAction } from "./workflowModel";
import { computeOutcome } from "./workflowModel";

type WizardState = WorkflowAnswers | null;

function optionKeys<T extends string>(t: TFunction, prefix: string, values: T[]) {
  return values.map((value) => ({ label: t(`${prefix}.${value}`), value }));
}

function OutcomeCard({ outcome }: { outcome: WorkflowOutcome }) {
  const { t } = useTranslation();
  return (
    <Alert
      type="success"
      showIcon
      message={t(outcome.labelKey)}
      description={
        <Space direction="vertical" size={4}>
          <Typography.Text type="secondary">{t("workflow.nextStepHint")}</Typography.Text>
        </Space>
      }
    />
  );
}

export function WorkflowWizard() {
  const { t } = useTranslation();
  const [state, setState] = useState<WizardState>(null);
  const outcome = useMemo(() => computeOutcome(state), [state]);
  const totalSteps = 3;

  const topIntent = (state && "topIntent" in state ? state.topIntent : null) as TopIntent | null;
  const newAction = topIntent === "new" ? (state as Extract<WorkflowAnswers, { topIntent: "new" }>).action : null;

  const yesNo = [
    { label: t("common.yes"), value: "yes" },
    { label: t("common.no"), value: "no" },
  ];

  return (
    <ProCard
      title={t("workflow.title")}
      extra={
        <Button
          onClick={() => {
            setState(null);
            message.info(t("workflow.resetInfo"));
          }}
        >
          {t("common.reset")}
        </Button>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        {t("workflow.intro")}
      </Typography.Paragraph>

      <StepsForm
        onFinish={async () => {
          const o = computeOutcome(state);
          if (!o) {
            message.error(t("workflow.completeFlow"));
            return false;
          }
          message.success(t(o.labelKey));
          return true;
        }}
        stepsProps={{ size: "small" }}
        submitter={{
          render: (props) => {
            return (
              <Space>
                {props.step > 0 && (
                  <Button onClick={() => props.onPre?.()}>{t("common.back")}</Button>
                )}
                {props.step < totalSteps - 1 ? (
                  <Button type="primary" onClick={() => props.onSubmit?.()}>
                    {t("common.continue")}
                  </Button>
                ) : (
                  <Button type="primary" onClick={() => props.onSubmit?.()}>
                    {t("common.start")}
                  </Button>
                )}
              </Space>
            );
          },
        }}
        formProps={{
          onValuesChange: (_, all) => {
            if (!all.topIntent) return;
            const ti = all.topIntent as TopIntent;
            if (ti === "new") {
              const next: Extract<WorkflowAnswers, { topIntent: "new" }> = {
                topIntent: "new",
                action: all.action as NewFirstAction,
                ...all,
              };
              setState(next);
            } else if (ti === "existing") {
              const next: Extract<WorkflowAnswers, { topIntent: "existing" }> = {
                topIntent: "existing",
                problem: all.problem,
                clarifier: all.clarifier,
              };
              setState(next);
            } else {
              const next: Extract<WorkflowAnswers, { topIntent: "partner" }> = {
                topIntent: "partner",
                network: all.network,
                offerComplianceServices: all.offerComplianceServices,
              };
              setState(next);
            }
          },
        }}
      >
        <StepsForm.StepForm
          name="intent"
          title={t("workflow.steps.intent")}
          onFinish={async (values) => {
            const ti = values.topIntent as TopIntent;
            if (ti === "new") {
              setState({ topIntent: "new", action: values.action as NewFirstAction });
            } else if (ti === "existing") {
              setState({ topIntent: "existing", problem: values.problem });
            } else {
              setState({ topIntent: "partner", network: values.network, offerComplianceServices: "no" });
            }
            return true;
          }}
        >
          <ProForm.Group>
            <ProFormRadio.Group
              name="topIntent"
              label={t("workflow.topIntent.label")}
              rules={[{ required: true }]}
              options={optionKeys(t, "workflow.topIntent", ["new", "existing", "partner"])}
            />
          </ProForm.Group>

          {topIntent === "new" && (
            <ProForm.Group>
              <ProFormSelect
                name="action"
                label={t("workflow.firstAction.label")}
                rules={[{ required: true }]}
                options={optionKeys(t, "workflow.firstAction", [
                  "find_buyer",
                  "find_seller",
                  "find_logistics",
                  "start_compliance",
                  "secure_transaction",
                ])}
              />
            </ProForm.Group>
          )}

          {topIntent === "existing" && (
            <ProForm.Group>
              <ProFormSelect
                name="problem"
                label={t("workflow.existingProblem.label")}
                rules={[{ required: true }]}
                options={optionKeys(t, "workflow.existingProblem", [
                  "missing_docs",
                  "pending_payment",
                  "shipment_delay",
                  "contract_verification",
                  "customs_issue",
                  "dispute",
                  "need_logistics_partner",
                  "audit_request",
                ])}
              />
            </ProForm.Group>
          )}

          {topIntent === "partner" && (
            <ProForm.Group>
              <ProFormSelect
                name="network"
                label={t("workflow.partnerNetwork.label")}
                rules={[{ required: true }]}
                options={optionKeys(t, "workflow.partnerNetwork", [
                  "buyer",
                  "seller",
                  "broker",
                  "logistics",
                  "financial",
                ])}
              />
            </ProForm.Group>
          )}
        </StepsForm.StepForm>

        <StepsForm.StepForm name="details" title={t("workflow.steps.details")} onFinish={async () => true}>
          {topIntent === "partner" && (
            <ProForm.Group>
              <ProFormRadio.Group
                name="offerComplianceServices"
                label={t("workflow.offerCompliance.label")}
                rules={[{ required: true }]}
                options={yesNo}
              />
            </ProForm.Group>
          )}

          {topIntent === "existing" && (
            <ProForm.Group>
              <ProFormText
                name="clarifier"
                label={t("workflow.clarifier.label")}
                placeholder={t("workflow.clarifier.placeholder")}
              />
            </ProForm.Group>
          )}

          {topIntent === "new" && newAction === "find_buyer" && (
            <>
              <ProForm.Group>
                <ProFormSelect
                  name="sellingWhat"
                  label={t("workflow.sellingWhat.label")}
                  rules={[{ required: true }]}
                  options={optionKeys(t, "workflow.sellingWhat", [
                    "commodity_lot",
                    "certified_goods",
                    "logistics_capacity",
                    "storage_capacity",
                  ])}
                />
              </ProForm.Group>
              <ProForm.Group>
                <ProFormRadio.Group
                  name="eudrRelevant"
                  label={t("workflow.eudrRelevant.label")}
                  rules={[{ required: true }]}
                  options={yesNo}
                />
              </ProForm.Group>
              <ProForm.Group>
                <ProFormSelect
                  name="docsStatus"
                  label={t("workflow.docsStatus.label")}
                  tooltip={t("workflow.docsStatus.tooltip")}
                  dependencies={["eudrRelevant"]}
                  rules={[{ required: true, message: t("workflow.docsStatus.required") }]}
                  request={async () =>
                    optionKeys(t, "workflow.docsStatus", ["full_dds", "partial", "need_support"])
                  }
                />
              </ProForm.Group>
              <ProForm.Group>
                <ProFormRadio.Group
                  name="paymentProtection"
                  label={t("workflow.paymentProtection.label")}
                  rules={[{ required: true }]}
                  options={yesNo}
                />
              </ProForm.Group>
            </>
          )}

          {topIntent === "new" && newAction === "find_seller" && (
            <>
              <ProForm.Group>
                <ProFormSelect
                  name="buyingWhat"
                  label={t("workflow.buyingWhat.label")}
                  rules={[{ required: true }]}
                  options={optionKeys(t, "workflow.buyingWhat", [
                    "agri_commodities",
                    "verified_eudr_goods",
                    "storage_services",
                    "logistics_services",
                  ])}
                />
              </ProForm.Group>
              <ProForm.Group>
                <ProFormRadio.Group
                  name="euImportInvolved"
                  label={t("workflow.euImportInvolved.label")}
                  rules={[{ required: true }]}
                  options={yesNo}
                />
              </ProForm.Group>
              <ProForm.Group>
                <ProFormSelect
                  name="supplierVerification"
                  label={t("workflow.supplierVerification.label")}
                  rules={[{ required: true }]}
                  options={optionKeys(t, "workflow.supplierVerification", ["mandatory", "preferred", "optional"])}
                />
              </ProForm.Group>
              <ProForm.Group>
                <ProFormSelect
                  name="handling"
                  label={t("workflow.handling.label")}
                  rules={[{ required: true }]}
                  options={optionKeys(t, "workflow.handling", ["escrow", "compliance_only", "standard"])}
                />
              </ProForm.Group>
            </>
          )}

          {topIntent === "new" && newAction === "find_logistics" && (
            <>
              <ProForm.Group>
                <ProFormSelect
                  name="logisticsMode"
                  label={t("workflow.logisticsMode.label")}
                  rules={[{ required: true }]}
                  options={optionKeys(t, "workflow.logisticsMode", [
                    "truck",
                    "rail",
                    "sea",
                    "multimodal",
                    "customs",
                  ])}
                />
              </ProForm.Group>
              <ProForm.Group>
                <ProFormRadio.Group
                  name="traceabilityRequired"
                  label={t("workflow.traceabilityRequired.label")}
                  rules={[{ required: true }]}
                  options={yesNo}
                />
              </ProForm.Group>
              <ProForm.Group>
                <ProFormSelect
                  name="shipmentVerification"
                  label={t("workflow.shipmentVerification.label")}
                  rules={[{ required: true }]}
                  options={optionKeys(t, "workflow.shipmentVerification", ["full", "basic", "recommend"])}
                />
              </ProForm.Group>
              <ProForm.Group>
                <ProFormRadio.Group
                  name="linkedToExistingTrade"
                  label={t("workflow.linkedToExistingTrade.label")}
                  rules={[{ required: true }]}
                  options={yesNo}
                />
              </ProForm.Group>
            </>
          )}

          {topIntent === "new" && newAction === "start_compliance" && (
            <>
              <ProForm.Group>
                <ProFormSelect
                  name="complianceProcess"
                  label={t("workflow.complianceProcess.label")}
                  rules={[{ required: true }]}
                  options={optionKeys(t, "workflow.complianceProcess", [
                    "dds",
                    "supplier_verification",
                    "shipment_traceability",
                    "audit_preparation",
                    "full_workflow",
                  ])}
                />
              </ProForm.Group>
              <ProForm.Group>
                <ProFormSelect
                  name="supplierDocsAvailability"
                  label={t("workflow.supplierDocsAvailability.label")}
                  rules={[{ required: true }]}
                  options={optionKeys(t, "workflow.supplierDocsAvailability", ["full", "partial", "none"])}
                />
              </ProForm.Group>
              <ProForm.Group>
                <ProFormSelect
                  name="externalVerification"
                  label={t("workflow.externalVerification.label")}
                  rules={[{ required: true }]}
                  options={optionKeys(t, "workflow.externalVerification", [
                    "satellite",
                    "document_validation",
                    "risk_scoring",
                    "full",
                  ])}
                />
              </ProForm.Group>
            </>
          )}

          {topIntent === "new" && newAction === "secure_transaction" && (
            <>
              <ProForm.Group>
                <ProFormSelect
                  name="secureWhat"
                  label={t("workflow.secureWhat.label")}
                  rules={[{ required: true }]}
                  options={optionKeys(t, "workflow.secureWhat", [
                    "payment",
                    "contract",
                    "delivery",
                    "full_transaction",
                    "trade_finance",
                  ])}
                />
              </ProForm.Group>
              <ProForm.Group>
                <ProFormRadio.Group
                  name="partiesIdentified"
                  label={t("workflow.partiesIdentified.label")}
                  rules={[{ required: true }]}
                  options={yesNo}
                />
              </ProForm.Group>
              <ProForm.Group>
                <ProFormRadio.Group
                  name="needComplianceToo"
                  label={t("workflow.needComplianceToo.label")}
                  rules={[{ required: true }]}
                  options={yesNo}
                />
              </ProForm.Group>
            </>
          )}
        </StepsForm.StepForm>

        <StepsForm.StepForm name="confirm" title={t("workflow.steps.confirm")} onFinish={async () => true}>
          {outcome ? (
            <OutcomeCard outcome={outcome} />
          ) : (
            <Alert type="info" showIcon message={t("workflow.completePrevious")} />
          )}
          <ProForm.Group style={{ marginTop: 12 }}>
            <ProFormText
              name="notes"
              label={t("workflow.optionalNote.label")}
              placeholder={t("workflow.optionalNote.placeholder")}
            />
          </ProForm.Group>
        </StepsForm.StepForm>
      </StepsForm>
    </ProCard>
  );
}

export default WorkflowWizard;
